/**
 * src/v2/services/pendingDepositService.ts
 * App-layer service for GeoLink-qualified pending deposits.
 *
 * Architecture note:
 *   - GeoLink qualification ≠ settlement.
 *   - PendingDeposit records live in the server's in-memory store.
 *   - GeoVault balances are only mutated when settlePendingDeposit() succeeds.
 *   - This service mirrors the vault.js deposit path for auth compatibility.
 *
 * Endpoints:
 *   POST /api/v2/geolink/ingest
 *   GET  /api/v2/geolink/pending-deposits
 *   GET  /api/v2/geolink/pending-deposits/:id
 *   POST /api/v2/geolink/pending-deposits/:id/reject
 *   POST /api/v2/geolink/pending-deposits/:id/settle
 */

import { V2_API } from '../config';
import type { PendingDeposit } from '../types';
import type { NormalizedRuleMatch } from '../../services/geolinkApi';

const GEOLINK_API = `${V2_API}/geolink`;

// ── Ingestion ─────────────────────────────────────────────────────────────────

export interface IngestResult {
  success: boolean;
  pendingDepositId?: string;
  geoTrustDecision?: PendingDeposit['geoTrustDecision'];
  decisionReason?: string | null;
  record?: PendingDeposit;
  error?: string;
}

/**
 * PART 3 — Step A/B/C/D
 * Send a GeoLink normalized rule match to the GeoTrust server for ingestion.
 * The server validates the payload, checks compliance policy, and creates
 * a PendingDeposit with geoTrustDecision = "pending" | "rejected".
 */
export async function ingestNormalizedRuleMatch(
  match: NormalizedRuleMatch,
): Promise<IngestResult> {
  const resp = await fetch(`${GEOLINK_API}/ingest`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(match),
  });
  return resp.json();
}

// ── List / get ────────────────────────────────────────────────────────────────

export interface ListPendingDepositsParams {
  walletPublicKey?: string;
  decision?: PendingDeposit['geoTrustDecision'];
  network?: 'testnet' | 'mainnet';
}

export interface ListPendingDepositsResult {
  success: boolean;
  count: number;
  pendingDeposits: PendingDeposit[];
}

/** Fetch all pending deposits (optional filters). */
export async function listPendingDeposits(
  params: ListPendingDepositsParams = {},
): Promise<PendingDeposit[]> {
  const url = new URL(`${GEOLINK_API}/pending-deposits`);
  if (params.walletPublicKey) url.searchParams.set('walletPublicKey', params.walletPublicKey);
  if (params.decision)        url.searchParams.set('decision',        params.decision);
  if (params.network)         url.searchParams.set('network',         params.network);

  const resp = await fetch(url.toString());
  if (!resp.ok) throw new Error(`Failed to list pending deposits: ${resp.status}`);

  const data: ListPendingDepositsResult = await resp.json();
  return data.pendingDeposits ?? [];
}

/** Fetch a single pending deposit by ID. */
export async function getPendingDeposit(pendingDepositId: string): Promise<PendingDeposit> {
  const resp = await fetch(`${GEOLINK_API}/pending-deposits/${encodeURIComponent(pendingDepositId)}`);
  if (!resp.ok) {
    const err: any = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || `PendingDeposit ${pendingDepositId} not found`);
  }
  const data: { success: boolean; pendingDeposit: PendingDeposit } = await resp.json();
  return data.pendingDeposit;
}

// ── Reject ────────────────────────────────────────────────────────────────────

export interface RejectResult {
  success: boolean;
  pendingDeposit?: PendingDeposit;
  error?: string;
}

/** Mark a pending deposit as rejected (user declined or policy override). */
export async function rejectPendingDeposit(
  pendingDepositId: string,
  reason?: string,
): Promise<RejectResult> {
  const resp = await fetch(
    `${GEOLINK_API}/pending-deposits/${encodeURIComponent(pendingDepositId)}/reject`,
    {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ reason: reason ?? 'User rejected' }),
    },
  );
  return resp.json();
}

// ── Settlement ────────────────────────────────────────────────────────────────

export interface SettleParams {
  pendingDepositId: string;
  /** Pre-signed XDR from Freighter — recommended path */
  signedXdr?: string;
  /**
   * When true the backend adds the service-account fee signature before
   * submitting.  Set for passkey (smart-account) signed XDRs whose transaction
   * source is the service account, not the user's wallet.
   */
  useServiceAccount?: boolean;
  /** WebAuthn path */
  webauthnSignature?:         string;
  webauthnAuthenticatorData?: string;
  webauthnClientData?:        string;
  signaturePayload?:          string;
  /**
   * Secret key path — passed directly to GeoLink for server-side signing.
   * GeoLink supports this for backward compat / testnet. Never use in production.
   */
  userSecretKey?: string;
  /** User's chosen deposit amount in stroops (overrides the rule's stored amount) */
  overrideAmount?: string;
  /** User's wallet public key (passed to GeoLink's execute endpoint) */
  userPublicKey?: string;
}

// ── Prepare unsigned XDR ──────────────────────────────────────────────────────

export interface PrepareXdrResult {
  success: boolean;
  unsignedXdr?: string;
  /** Latest ledger at simulation time — needed for passkey auth-entry expiry. */
  latestLedger?: number;
  network?: string;
  amountStroops?: number;
  contractAddress?: string;
  functionName?: string;
  error?: string;
}

/**
 * Ask the backend to build an unsigned Soroban transaction XDR for a pending
 * deposit with a user-specified amount.  The browser then signs it and
 * submits via settlePendingDeposit.
 *
 * @param useServiceAccount  Pass true for passkey (smart-account) users so
 *   the backend uses the service account as the transaction source/fee payer.
 *   The smart account then only signs the Soroban auth entry.
 */
export async function prepareDepositXdr(
  pendingDepositId: string,
  amountStroops: string | number,
  userPublicKey: string,
  options?: { useServiceAccount?: boolean },
): Promise<PrepareXdrResult> {
  const resp = await fetch(`${GEOLINK_API}/prepare-deposit-xdr`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      pendingDepositId,
      amountStroops: String(amountStroops),
      userPublicKey,
      useServiceAccount: options?.useServiceAccount ?? false,
    }),
  });
  return resp.json();
}

export interface SettleResult {
  success: boolean;
  pendingDepositId?: string;
  settlementTxHash?: string;
  settlementLedger?: number;
  pendingDeposit?: PendingDeposit;
  error?: string;
  code?: number;
}

/**
 * PART 6 — settlePendingDeposit
 *
 * 1. Loads the pending deposit (server-side).
 * 2. Server re-checks compliance policy.
 * 3. Calls the existing GeoVault deposit path.
 * 4. Marks the record as settled on success.
 * 5. Returns clear error states on failure.
 *
 * Auth: provide exactly one of signedXdr, WebAuthn fields, or userSecretKey.
 * Duplicate settlement is rejected by the server (409).
 */
export async function settlePendingDeposit(params: SettleParams): Promise<SettleResult> {
  const { pendingDepositId, ...authFields } = params;

  const resp = await fetch(
    `${GEOLINK_API}/pending-deposits/${encodeURIComponent(pendingDepositId)}/settle`,
    {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(authFields),
    },
  );

  const data: SettleResult = await resp.json();

  if (!resp.ok && data.success !== false) {
    // Normalise HTTP errors that didn't include a structured body
    return { success: false, error: `HTTP ${resp.status} ${resp.statusText}` };
  }

  return data;
}

// ── GeoLink sync ─────────────────────────────────────────────────────────────

export interface SyncResult {
  success: boolean;
  newDepositsCount: number;
  skippedCount: number;
  newDeposits: PendingDeposit[];
  skipped: string[];
  error?: string;
}

/**
 * Pull GeoLink's pending-rules queue and auto-ingest any new entries into
 * GeoTrust.  Call this periodically (e.g. on every poll tick) so that rule
 * matches fired by GeoLink appear in the pending-deposits panel within one
 * cycle.
 *
 * @param walletPublicKey  Optionally narrow the GeoLink query to one wallet.
 */
export async function syncFromGeoLink(walletPublicKey?: string): Promise<SyncResult> {
  const resp = await fetch(`${GEOLINK_API}/sync`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ walletPublicKey }),
  });

  const data: SyncResult = await resp.json().catch(() => ({
    success: false,
    newDepositsCount: 0,
    skippedCount: 0,
    newDeposits: [],
    skipped: [],
    error: `HTTP ${resp.status}`,
  }));

  return data;
}

// ── Full ingestion + settlement flow ─────────────────────────────────────────

/**
 * Complete GeoLink → GeoTrust ingestion flow (PART 3 Steps A–E).
 *
 * Usage: call ingestThenSettle() when the user confirms a qualified GeoLink
 * match directly in the UI and wants to immediately settle.
 *
 * For deferred settlement (show UI first, then settle on user action), use
 * ingestNormalizedRuleMatch() + settlePendingDeposit() separately.
 */
export async function ingestThenSettle(
  match: NormalizedRuleMatch,
  authParams: Omit<SettleParams, 'pendingDepositId'>,
): Promise<SettleResult> {
  const ingestResult = await ingestNormalizedRuleMatch(match);

  if (!ingestResult.success || !ingestResult.pendingDepositId) {
    return {
      success: false,
      error: ingestResult.error || 'Ingestion failed',
    };
  }

  if (ingestResult.geoTrustDecision === 'rejected') {
    return {
      success:         false,
      pendingDepositId: ingestResult.pendingDepositId,
      error:           ingestResult.decisionReason || 'Deposit rejected by GeoTrust policy',
    };
  }

  return settlePendingDeposit({
    pendingDepositId: ingestResult.pendingDepositId,
    ...authParams,
  });
}
